import { serialize } from 'object-to-formdata'
import snakecaseKeys from 'snakecase-keys'
import axiosIns from '@/plugins/axios'

export default {
  /**
   * セルフオーダー設定を取得
   * @returns {Promise} 設定データ
   */
  getSelfOrderSetting() {
    return axiosIns.get('/admin/self_order_setting')
  },

  /**
   * セルフオーダー設定を更新
   * @param {Object} params 設定パラメータ
   * @param {string} params.primaryColor プライマリカラー
   * @param {string} params.backgroundColor 背景カラー
   * @param {string} params.cardColor カードカラー
   * @param {string} params.textColor テキストカラー
   * @param {File} [params.logo] ロゴ画像
   * @param {File} [params.banner] バナー画像
   * @param {boolean} [params.removeLogo] ロゴを削除するフラグ
   * @param {boolean} [params.removeBanner] バナーを削除するフラグ
   * @param {boolean} [params.nominationEnabled] セルフオーダーで指名を有効にするフラグ
   * @param {boolean} [params.courseEnabled] セルフオーダーでコースを有効にするフラグ
   * @param {number|string} [params.staffCallVendibleTagId] スタッフ呼び出しタグID（''でクリア）
   * @param {string} [params.staffCallPrintTarget] スタッフ呼び出しの印刷先（'kitchen' | 'receipt'）
   * @returns {Promise} 更新後の設定データ
   */
  updateSelfOrderSetting({
    primaryColor,
    backgroundColor,
    cardColor,
    textColor,
    logo,
    banner,
    removeLogo,
    removeBanner,
    nominationEnabled,
    courseEnabled,
    staffCallVendibleTagId,
    staffCallPrintTarget,
  }) {
    const settingData = {
      primaryColor,
      backgroundColor,
      cardColor,
      textColor,
      nominationEnabled,
      courseEnabled,
      staffCallVendibleTagId,
      staffCallPrintTarget,
    }
    const formData = serialize(snakecaseKeys({ selfOrderSetting: settingData }))

    // ファイルは直接FormDataに追加
    if (logo instanceof File) {
      formData.append('self_order_setting[logo]', logo)
    }
    if (banner instanceof File) {
      formData.append('self_order_setting[banner]', banner)
    }

    // 削除フラグ（top-level param として送る）
    if (removeLogo) {
      formData.append('remove_logo', 'true')
    }
    if (removeBanner) {
      formData.append('remove_banner', 'true')
    }

    return axiosIns.patch('/admin/self_order_setting', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  /**
   * カテゴリを追加
   * @param {number} vendibleTagId VendibleTag ID
   * @param {string} icon アイコン識別子
   * @returns {Promise} 追加されたカテゴリ
   */
  addCategory(vendibleTagId, icon = 'default') {
    return axiosIns.post('/admin/self_order_setting/add_category', {
      vendible_tag_id: vendibleTagId,
      icon,
    })
  },

  /**
   * カテゴリを削除
   * @param {number} vendibleTagId VendibleTag ID
   * @returns {Promise} 削除結果
   */
  removeCategory(vendibleTagId) {
    return axiosIns.delete('/admin/self_order_setting/remove_category', {
      data: { vendible_tag_id: vendibleTagId },
    })
  },

  /**
   * カテゴリの並び順を更新
   * @param {Array<number>} categoryIds VendibleTag IDの配列（順序通り）
   * @returns {Promise} 更新後の設定データ
   */
  reorderCategories(categoryIds) {
    return axiosIns.put('/admin/self_order_setting/reorder_categories', {
      category_ids: categoryIds,
    })
  },

  /**
   * カテゴリのアイコンを更新
   * @param {number} vendibleTagId VendibleTag ID
   * @param {string} icon アイコン識別子
   * @returns {Promise} 更新後のカテゴリ
   */
  updateCategoryIcon(vendibleTagId, icon) {
    return axiosIns.patch('/admin/self_order_setting/update_category_icon', {
      vendible_tag_id: vendibleTagId,
      icon,
    })
  },

  /**
   * 利用可能なVendibleTagを取得
   * @returns {Promise} VendibleTag一覧
   */
  getVendibleTags() {
    return axiosIns.get('/admin/vendible_tags')
  },

  /**
   * カテゴリに紐づく商品を取得
   * @param {number} vendibleTagId VendibleTag ID
   * @returns {Promise} 商品一覧
   */
  getCategoryItems(vendibleTagId) {
    return axiosIns.get('/admin/self_order_setting/category_items', {
      params: { vendible_tag_id: vendibleTagId },
    })
  },

  /**
   * 商品画像を更新
   * @param {number} itemId 商品ID
   * @param {File} image 画像ファイル
   * @returns {Promise} 画像URL
   */
  updateItemImage(itemId, image) {
    const formData = new FormData()
    formData.append('image', image)

    return axiosIns.patch(`/admin/vendibles/${itemId}/update_image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  /**
   * 商品画像を削除
   * @param {number} itemId 商品ID
   * @returns {Promise} 削除結果
   */
  removeItemImage(itemId) {
    return axiosIns.delete(`/admin/vendibles/${itemId}/remove_image`)
  },

  /**
   * QRコード一覧を取得
   * @returns {Promise} QRコード一覧（ホール・テーブル別）
   */
  getQrCodes() {
    return axiosIns.get('/admin/self_order_setting/qr_codes')
  },

  /**
   * ホールのpublic_idをリセット（QRコードURL無効化）
   * @param {number} hallId ホールID
   * @returns {Promise} リセット結果
   */
  resetHallPublicId(hallId) {
    return axiosIns.post('/admin/self_order_setting/reset_hall_public_id', {
      hall_id: hallId,
    })
  },
}
